import { z } from 'zod';
import { Bet } from './bet.ts';
import { GameDefinition, GameId } from './game.ts';
import { COUNTRY_CODES } from './countries.ts';
import { UtcInstant } from './primitives.ts';
import { Promotion, RewardGrantCommand } from './promotion.ts';

/** ISO 3166-1 alpha-2 (an assigned code, see countries.ts), as declared by the operator for the player's jurisdiction. */
export const CountryCode = z
  .string()
  .regex(/^[A-Z]{2}$/)
  .refine((c) => COUNTRY_CODES.has(c), { message: 'unknown ISO 3166-1 alpha-2 country code' });
const CurrencyCode = z.string().regex(/^[A-Z0-9]{2,10}$/);

/**
 * Partner API v1 launch. The wallet currency is the brand's (authoritative); `currency`, when sent, is
 * only an assertion that must match it. `displayCurrency` is presentation only. `country` is the
 * operator-declared jurisdiction checked against the brand's and game's entitlement — never inferred
 * from an IP address.
 */
export const PartnerLaunchRequest = z
  .object({
    brandId: z.string().min(1).max(64),
    externalPlayerId: z.string().min(1).max(128),
    gameId: GameId,
    country: CountryCode,
    currency: CurrencyCode.optional(),
    displayCurrency: CurrencyCode.optional(),
    language: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).optional(),
    allowedOrigin: z.string().url().max(256).nullable().optional(),
    /** Operator's own wallet-session token: echoed on every wallet callback (opaque to us, printable ASCII). */
    operatorToken: z.string().min(1).max(512).regex(/^[\x21-\x7E]+$/).optional(),
    device: z.enum(['desktop', 'mobile']).optional(),
    /** Absolute https URLs; where the game sends the player (lobby / cashier / return). */
    lobbyUrl: z.string().url().max(2048).optional(),
    depositUrl: z.string().url().max(2048).optional(),
    returnUrl: z.string().url().max(2048).optional(),
    /** Aggregators: display name of the site sent as brandId (used when the site is created on first launch). */
    siteName: z.string().min(1).max(120).optional(),
  })
  .strict();
export type PartnerLaunchRequest = z.infer<typeof PartnerLaunchRequest>;

/** Demo (fun-play) launch: an anonymous demo player on our demo wallet; no call reaches the operator wallet. */
export const DemoLaunchRequest = z
  .object({
    brandId: z.string().min(1).max(64),
    gameId: GameId,
    country: CountryCode,
    language: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).optional(),
    allowedOrigin: z.string().url().max(256).nullable().optional(),
    device: z.enum(['desktop', 'mobile']).optional(),
    lobbyUrl: z.string().url().max(2048).optional(),
    /** Aggregators: the site's wallet currency (required when the site may use several) and display name. */
    currency: CurrencyCode.optional(),
    siteName: z.string().min(1).max(120).optional(),
  })
  .strict();
export type DemoLaunchRequest = z.infer<typeof DemoLaunchRequest>;

/** A player's own statistics (counts and per-currency money; never summed across currencies). */
export const PlayerStats = z.object({
  alias: z.string(),
  firstPlayed: z.string().nullable(),
  totals: z.object({ rounds: z.number().int(), bets: z.number().int(), wins: z.number().int(), exactHits: z.number().int(), bestMultiplier: z.number(), playMinutes: z.number().int() }),
  last30Days: z.object({ rounds: z.number().int(), bets: z.number().int(), wins: z.number().int() }),
  money: z.array(z.object({ currency: z.string(), scale: z.number().int(), stake: z.string(), payout: z.string(), net: z.string(), biggestWin: z.string() })),
  favouriteSelection: z.string().nullable(),
  recent: z.array(z.object({ gameRoundId: z.string(), at: z.string(), selection: z.string(), stake: z.string(), payout: z.string(), currency: z.string(), scale: z.number().int(), finalCount: z.number().int().nullable(), low: z.number().int().nullable(), high: z.number().int().nullable(), funding: z.string() })),
});
export type PlayerStats = z.infer<typeof PlayerStats>;

export const PartnerLaunchResponse = z.object({
  launchCode: z.string(),
  launchUrl: z.string(),
  expiresInS: z.number().int(),
  currency: z.string(),
  displayCurrency: z.string().nullable(),
  /** Our brand id for this launch (for aggregators: the brand of the site and currency). */
  brandId: z.string(),
});

export const CatalogueEntry = GameDefinition.extend({
  /** Brands of this operator that may offer the game, with their country allowlists (empty = brand default). */
  entitlements: z.array(z.object({ brandId: z.string(), countries: z.array(CountryCode), moneyMode: z.enum(['DEMO', 'REAL']).optional() })),
});

export const PartnerRewardGrant = z.object({ brandId: z.string().min(1).max(64), externalPlayerId: z.string().min(1).max(128), grant: RewardGrantCommand.strict() }).strict();

export const WebhookEventType = z.enum(['bet.accepted', 'bet.rejected', 'bet.settled', 'bet.refunded', 'bet.compensated', 'reward.granted', 'reward.cancelled', 'reward.consumed', 'reward.returned', 'webhook.test']);
export type WebhookEventType = z.infer<typeof WebhookEventType>;

/** Event delivered to operator webhooks (at-least-once; deduplicate by eventId; replays keep the eventId). */
export const WebhookEvent = z.object({
  apiVersion: z.literal('1'),
  eventId: z.string().min(1).max(160),
  type: WebhookEventType,
  occurredAt: UtcInstant,
  operatorId: z.string(),
  data: z.object({ bet: Bet.optional(), promotion: Promotion.optional(), message: z.string().optional() }),
});
export type WebhookEvent = z.infer<typeof WebhookEvent>;

export const WebhookEndpointRequest = z.object({ url: z.string().url().max(512), events: z.array(WebhookEventType).min(1) }).strict();

export const WebhookDelivery = z.object({
  deliveryId: z.string(),
  endpointId: z.string(),
  eventId: z.string(),
  eventType: z.string(),
  state: z.enum(['PENDING', 'DELIVERED', 'FAILED', 'DEAD']),
  attempts: z.number().int(),
  lastStatus: z.number().int().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  deliveredAt: z.string().nullable(),
  replayOf: z.string().nullable(),
});

/**
 * Error semantics of the partner API. `retry`: SAFE = resend the same request (new nonce, same business
 * ids); NO = do not resend unchanged; AFTER_FIX = change credentials/config first.
 */
export const PARTNER_ERRORS = {
  BAD_REQUEST: { status: 400, retry: 'NO', meaning: 'Body/query does not match the schema (unknown fields are rejected).' },
  SIGNATURE_MISSING: { status: 401, retry: 'AFTER_FIX', meaning: 'x-ur-key-id, x-ur-timestamp, x-ur-nonce and x-ur-signature are required.' },
  NONCE_INVALID: { status: 401, retry: 'AFTER_FIX', meaning: 'Nonce must be 16–64 characters [A-Za-z0-9_-].' },
  TIMESTAMP_SKEW: { status: 401, retry: 'AFTER_FIX', meaning: 'Timestamp more than 60 s from our clock; fix your clock (NTP).' },
  KEY_UNKNOWN: { status: 401, retry: 'AFTER_FIX', meaning: 'Unknown key id.' },
  KEY_REVOKED: { status: 401, retry: 'AFTER_FIX', meaning: 'Key revoked; use your current key.' },
  KEY_EXPIRED: { status: 401, retry: 'AFTER_FIX', meaning: 'Key expired (rotation overlap ended).' },
  SIGNATURE_INVALID: { status: 401, retry: 'AFTER_FIX', meaning: 'Signature does not match the canonical request.' },
  NONCE_REPLAY: { status: 401, retry: 'SAFE', meaning: 'Nonce already used: resend with a new nonce (business ids unchanged).' },
  IP_NOT_ALLOWED: { status: 403, retry: 'AFTER_FIX', meaning: 'Source address outside the key allowlist.' },
  SCOPE_MISSING: { status: 403, retry: 'AFTER_FIX', meaning: 'Key lacks the scope for this route.' },
  NOT_FOUND: { status: 404, retry: 'NO', meaning: 'Not found in your tenant (other tenants’ ids are indistinguishable from unknown ids).' },
  GAME_NOT_ENTITLED: { status: 403, retry: 'NO', meaning: 'Brand is not entitled to this game.' },
  OPERATOR_SUSPENDED: { status: 403, retry: 'AFTER_FIX', meaning: 'Operator is suspended by UrbanRats; launches are refused until it is reactivated.' },
  DEMO_PLAYER_RESERVED: { status: 400, retry: 'NO', meaning: 'Player ids starting with demo: are reserved for demo sessions (POST /v1/demo-sessions).' },
  NAVIGATION_URL_INVALID: { status: 400, retry: 'NO', meaning: 'lobbyUrl, depositUrl and returnUrl must be absolute https URLs without credentials.' },
  GAME_RETIRED: { status: 403, retry: 'NO', meaning: 'The game is no longer offered; use the catalogue (GET /v1/games).' },
  CURRENCY_REQUIRED: { status: 400, retry: 'AFTER_FIX', meaning: 'Aggregators: send currency (the site or the template allows several wallet currencies).' },
  CURRENCY_NOT_ALLOWED: { status: 409, retry: 'NO', meaning: 'Aggregators: the wallet currency is not enabled in your aggregator template.' },
  SITE_LIMIT: { status: 403, retry: 'AFTER_FIX', meaning: 'Aggregators: the agreed maximum number of sites is reached; ask UrbanRats to raise it.' },
  BRAND_SUSPENDED: { status: 403, retry: 'AFTER_FIX', meaning: 'Brand (site) is suspended; launches are refused until it is reactivated.' },
  COUNTRY_NOT_ALLOWED: { status: 403, retry: 'NO', meaning: 'Declared country not allowed for this brand/game.' },
  REAL_MONEY_UNAVAILABLE: { status: 503, retry: 'AFTER_FIX', meaning: 'The site is in real-money mode but your seamless wallet is not connected; real-money launches resume when it is.' },
  CURRENCY_MISMATCH: { status: 409, retry: 'NO', meaning: 'Asserted currency differs from the brand wallet currency.' },
  DISPLAY_CURRENCY_NOT_ALLOWED: { status: 400, retry: 'NO', meaning: 'Display currency not enabled for the brand.' },
  ORIGIN_NOT_ALLOWED: { status: 400, retry: 'NO', meaning: 'Embed origin not in the brand allowlist.' },
  IDEMPOTENCY_CONFLICT: { status: 409, retry: 'NO', meaning: 'Same idempotency id reused with a different payload.' },
  REWARD_NOT_CANCELLABLE: { status: 409, retry: 'NO', meaning: 'Reward already reserved/consumed/expired.' },
  CALLBACK_HOST_NOT_REGISTERED: { status: 400, retry: 'AFTER_FIX', meaning: 'Callback host is not registered for your operator.' },
  CALLBACK_URL_REJECTED: { status: 400, retry: 'AFTER_FIX', meaning: 'Callback URL scheme/credentials/address not allowed (https, public address, no redirects).' },
  RATE_LIMITED: { status: 429, retry: 'SAFE', meaning: 'Slow down; retry with backoff.' },
} as const;
export type PartnerErrorCode = keyof typeof PARTNER_ERRORS;

